import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOSchema from '../../components/feature/SEOSchema';
import BlogCard from './components/BlogCard';
import { blogPosts, blogCategories } from '../../mocks/blogPosts';

const siteUrl = 'https://destinygpt.com';

const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${siteUrl}/blog`,
  url: `${siteUrl}/blog`,
  name: '데스티니AI 포춘 블로그 — 점성술·사주 운세 가이드',
  description: '데스티니AI 포춘 공식 블로그. 점성술, 사주팔자, 꿈해몽, 연간 운세, Destiny GPT 활용법 등 운명 분석 콘텐츠를 제공합니다.',
  inLanguage: 'ko-KR',
  publisher: {
    '@type': 'Organization',
    name: '데스티니GPT',
    url: siteUrl,
  },
  blogPost: blogPosts.map((p) => ({
    '@type': 'BlogPosting',
    headline: p.title,
    url: `${siteUrl}/blog/${p.slug}`,
    datePublished: p.publishedAt,
    author: { '@type': 'Organization', name: p.author },
    description: p.excerpt,
    keywords: p.tags.join(', '),
  })),
};

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = blogPosts.filter((post) => {
    const matchCategory = activeCategory === 'all' || post.category === activeCategory;
    const matchSearch =
      searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchCategory && matchSearch;
  });

  const featuredPosts = filteredPosts.filter((p) => p.featured);
  const regularPosts = filteredPosts.filter((p) => !p.featured);

  return (
    <div className="min-h-screen bg-[#06030f] text-white">
      <SEOSchema schema={blogSchema} id="blog-schema" />
      <Navbar />

      {/* ── HEADER ── */}
      <header className="relative pt-28 pb-16 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b66e1a33033b402a7f545a76e1e5d108.jpg"
            alt="데스티니AI 포춘 블로그"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#06030f]/60 via-[#06030f]/50 to-[#06030f]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <nav className="flex items-center justify-center gap-2 text-xs text-purple-400/60 mb-6">
            <Link to="/" className="hover:text-primary-gold transition-colors cursor-pointer">홈</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-purple-300/80">운세 블로그</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/10 border border-primary-gold/30 text-primary-gold text-xs font-semibold tracking-widest uppercase mb-5">
            <i className="ri-article-line"></i>
            데스티니AI 포춘 공식 블로그
          </div>

          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            점성술 · 사주 · 운세{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold to-amber-300">
              완전 가이드
            </span>
          </h1>
          <p className="text-purple-300/70 text-base max-w-xl mx-auto mb-8 leading-relaxed">
            데스티니AI 포춘 운세 연구팀이 전하는 점성술·사주팔자·꿈해몽·Destiny GPT 활용 가이드
          </p>

          {/* Search bar */}
          <div className="max-w-lg mx-auto relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-base"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="점성술, 사주팔자, 꿈해몽, 데스티니 GPT 검색..."
              className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white border border-white/30 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-gold/60 text-sm font-medium"
            />
          </div>
        </div>
      </header>

      {/* ── CATEGORY TABS ── */}
      <div className="sticky top-16 sm:top-20 z-40 bg-[#06030f]/95 backdrop-blur-xl border-b border-white/8 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {blogCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-primary-gold/20 border border-primary-gold/50 text-primary-gold'
                  : 'text-purple-300/60 hover:text-purple-200 border border-transparent hover:border-white/10'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        {filteredPosts.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-search-line text-4xl text-purple-400/30 mb-4 block"></i>
            <p className="text-purple-400/60">검색 결과가 없습니다.</p>
          </div>
        ) : (
          <>
            {/* Featured Posts */}
            {featuredPosts.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-primary-gold rounded-full"></div>
                  <h2 className="text-lg font-bold text-white">추천 아티클</h2>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {featuredPosts.map((post) => (
                    <BlogCard key={post.id} post={post} variant="featured" />
                  ))}
                </div>
              </section>
            )}

            {/* Regular Posts */}
            {regularPosts.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-purple-500 rounded-full"></div>
                  <h2 className="text-lg font-bold text-white">최신 아티클</h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {regularPosts.map((post) => (
                    <BlogCard key={post.id} post={post} variant="default" />
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* Tags Cloud */}
        <section className="mt-16 pt-10 border-t border-white/8">
          <h2 className="text-base font-bold text-white mb-5">인기 태그</h2>
          <div className="flex flex-wrap gap-2">
            {Array.from(new Set(blogPosts.flatMap((p) => p.tags))).map((tag) => (
              <button
                key={tag}
                onClick={() => setSearchQuery(tag)}
                className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-purple-300/70 text-xs hover:border-primary-gold/30 hover:text-primary-gold transition-all cursor-pointer"
              >
                #{tag}
              </button>
            ))}
          </div>
        </section>

        {/* CTA to main */}
        <section className="mt-12 rounded-2xl border border-primary-gold/20 bg-gradient-to-br from-primary-gold/5 to-violet-900/10 p-8 text-center">
          <h2 className="text-2xl font-black text-white mb-3">
            지금 바로 <span className="text-primary-gold">데스티니AI 포춘</span> 체험하기
          </h2>
          <p className="text-purple-300/60 text-sm mb-6">생년월일을 입력하면 AI가 3초 내로 운명을 분석합니다. 첫 분석 무료!</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-primary-gold to-amber-400 text-black font-bold rounded-xl hover:scale-105 transition-transform whitespace-nowrap cursor-pointer"
          >
            <i className="ri-flashlight-line"></i>
            무료 운세 분석 시작
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
